import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { logger } from '../utils/logger';
import { toast } from 'sonner';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const checkAuth = useCallback(async () => {
    try {
      // Cookie-based auth: try /me endpoint with credentials
      const response = await fetch(`${BACKEND_URL}/api/customers/me`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomer(data);
      }
    } catch (error) {
      logger.error('Auth check failed:', error);
    }
    setLoading(false);
  }, [BACKEND_URL]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Login failed');
      }

      const data = await response.json();
      // Token is in httpOnly cookie, only store non-sensitive data
      setCustomer(data.customer);
      toast.success('Succesvol ingelogd!');
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const register = async (customerData) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/customers/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(customerData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Registration failed');
      }

      const data = await response.json();
      // Token is in httpOnly cookie, only store non-sensitive data
      setCustomer(data.customer);
      toast.success('Account succesvol aangemaakt!');
      return true;
    } catch (error) {
      toast.error(error.message);
      return false;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/customers/logout/`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (_e) {
      // Logout API failure is non-critical - clear local state regardless
      console.warn('Customer logout API call failed');
    }
    setCustomer(null);
    toast.success('Uitgelogd');
  };

  const value = {
    customer,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!customer
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
